import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { invariant } from '@apollo/client/utilities/globals';
import { Alert, Table, TablePaginationConfig, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { useTableParams } from 'business/providers/table-params';
import { PAGE_OPTIONS } from 'business/quality-control/constants';
import { useGetSegmentHistoryData } from 'business/quality-control/pages/segment-history/hooks/data';
import { SegmentHistory } from 'business/quality-control/pages/segment-history/hooks/data/types';
import { formatFilterParams } from 'business/quality-control/pages/segment-history/hooks/data/utils';
import { useSegmentHistoryColumns } from 'business/quality-control/pages/segment-history/hooks/use-columns';
import { useSegmentHistoryExpandedColumns } from 'business/quality-control/pages/segment-history/hooks/use-expanded-columns';
import { SegmentMobileDrawer } from 'business/quality-control/pages/segment-history/mobile/components/segment-drawer';
import { useSegmentHistoryFilters } from 'business/quality-control/pages/segment-history/providers/filters';
import { Quality_SegmentHistoryStatusEnum_Enum } from 'generated/graphql';
import { FilterDataSetter } from 'technical/filter-data';

import { AddManualScanModal } from './add-manuel-scan-modal';
import { BulkStatusConfirmModal } from './bulk-status-confirm-modal';
import { BulkStatusModal } from './bulk-status-modal';
import { BulkStatusSelectionBar } from './bulk-status-selection-bar';
import { SegmentDetailsDrawer } from './segment-details-drawer';
import { UpdateSegmentModal } from './update-segment-modal';
import { SegmentHistoryTableFilterData } from '..';

export const SegmentTable = ({
  setFilterData,
  mobile = false,
  bulkStatusModalOpen = false,
  onCloseBulkStatusModal,
}: {
  setFilterData: FilterDataSetter<SegmentHistoryTableFilterData>;
  mobile?: boolean;
  bulkStatusModalOpen?: boolean;
  onCloseBulkStatusModal?: () => void;
}) => {
  const { t } = useTranslation();

  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site selected');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMobileOpen, setDrawerMobileOpen] = useState(false);
  const [updateSegmentModalOpen, setUpdateSegmentModalOpen] = useState(false);
  const [addManualScanModalOpen, setAddManualScanModalOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<SegmentHistory | null>(
    null,
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>(
    [],
  );

  const [pendingBulkValues, setPendingBulkValues] = useState<{
    newStatus: Quality_SegmentHistoryStatusEnum_Enum;
    scanSiteId: string;
  } | null>(null);
  const [includedSegmentIds, setIncludedSegmentIds] = useState<Set<string>>(
    new Set(),
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const selectionModeActive = pendingBulkValues !== null;
  const activationFiltersKeyRef = useRef<string | null>(null);
  const activationSortKeyRef = useRef<string | null>(null);

  const {
    isDataManager,
    modules: {
      QUALITY_SCAN: { isConstructionSiteManager },
    },
  } = usePermissions();

  const { tableParams, setTableParams, defaultTableParams } = useTableParams();

  const { data, total, loading, error, refetch } =
    useGetSegmentHistoryData(setFilterData);

  const filters = useSegmentHistoryFilters();
  const filtersKey = useMemo(
    () => JSON.stringify(formatFilterParams(filters)),
    [filters],
  );
  const sortKey = `${tableParams.sortField}|${tableParams.sortOrder}`;

  const firstRowSelection = useCallback(
    () => (data && data.length > 0 ? new Set([data[0].id]) : new Set<string>()),
    [data],
  );

  const resetPartialSelection = useCallback(() => {
    setPendingBulkValues(null);
    setIncludedSegmentIds(new Set());
    setConfirmModalOpen(false);
    activationFiltersKeyRef.current = null;
    activationSortKeyRef.current = null;
  }, []);

  const handleStartPartialSelection = (values: {
    newStatus: Quality_SegmentHistoryStatusEnum_Enum;
    scanSiteId: string;
  }) => {
    setPendingBulkValues(values);
    setIncludedSegmentIds(firstRowSelection());
    activationFiltersKeyRef.current = filtersKey;
    activationSortKeyRef.current = sortKey;
  };

  useEffect(() => {
    if (
      selectionModeActive &&
      activationFiltersKeyRef.current !== null &&
      activationFiltersKeyRef.current !== filtersKey
    ) {
      resetPartialSelection();
    }
  }, [filtersKey, selectionModeActive, resetPartialSelection]);

  useEffect(() => {
    if (
      selectionModeActive &&
      activationSortKeyRef.current !== null &&
      activationSortKeyRef.current !== sortKey
    ) {
      setIncludedSegmentIds(firstRowSelection());
      activationSortKeyRef.current = sortKey;
    }
  }, [sortKey, selectionModeActive, firstRowSelection]);

  const rowSelection: TableProps<SegmentHistory>['rowSelection'] =
    selectionModeActive
      ? {
          type: 'checkbox',
          selectedRowKeys: (data ?? [])
            .filter((segment) => includedSegmentIds.has(segment.id))
            .map((segment) => segment.id),
          onChange: (selectedRowKeys) => {
            const pageIds = (data ?? []).map((segment) => segment.id);
            setIncludedSegmentIds((prev) => {
              const next = new Set(prev);
              pageIds.forEach((id) => {
                if (selectedRowKeys.includes(id)) {
                  next.add(id);
                } else {
                  next.delete(id);
                }
              });
              return next;
            });
          },
        }
      : undefined;

  const handlePartialUpdateSuccess = () => {
    refetch();
    resetPartialSelection();
  };

  const handleOpenDrawer = (segment: SegmentHistory) => {
    setSelectedSegment(segment);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const handleOpenMobileDrawer = (segment: SegmentHistory) => {
    setSelectedSegment(segment);
    setDrawerMobileOpen(true);
  };

  const handleCloseMobileDrawer = () => {
    setDrawerMobileOpen(false);
  };

  const handleAfterOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedSegment(null);
    }
  };

  const handleOpenUpdateSegmentModal = (segment: SegmentHistory) => {
    setSelectedSegment(segment);
    setUpdateSegmentModalOpen(true);
  };

  const handleCloseUpdateSegmentModal = () => {
    setSelectedSegment(null);
    setUpdateSegmentModalOpen(false);
  };

  const handleOpenAddManualScanModal = (segment: SegmentHistory) => {
    setSelectedSegment(segment);
    setUpdateSegmentModalOpen(false);
    setAddManualScanModalOpen(true);
  };

  const handleCloseAddManualScanModal = () => {
    setSelectedSegment(null);
    setAddManualScanModalOpen(false);
  };

  const columns = useSegmentHistoryColumns(
    currentConstructionSiteId,
    handleOpenDrawer,
    handleOpenMobileDrawer,
    handleOpenUpdateSegmentModal,
    mobile,
    isDataManager,
    isConstructionSiteManager,
    refetch,
  );
  const expandedColumns = useSegmentHistoryExpandedColumns(
    isDataManager || isConstructionSiteManager,
    refetch,
  );

  const quarantineSegments = useMemo(() => {
    return (
      data?.filter((s) =>
        s.history.some(
          (history) =>
            history.status ===
              Quality_SegmentHistoryStatusEnum_Enum.Quarantine ||
            (history.status ===
              Quality_SegmentHistoryStatusEnum_Enum.Rejected &&
              history.segmentRefusal != null),
        ),
      ) ?? []
    );
  }, [data]);

  if (error) {
    return <Alert message={t('errors.error_generic')} type="error" />;
  }

  const handleChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter: SorterResult<SegmentHistory> | SorterResult<SegmentHistory>[],
  ) => {
    let sortByColumn;
    if (Array.isArray(sorter)) {
      sortByColumn = sorter[0];
    } else {
      sortByColumn = sorter;
    }

    setTableParams({
      current: pagination.current ?? defaultTableParams.current,
      pageSize: pagination.pageSize ?? defaultTableParams.pageSize,
      sortOrder: sortByColumn.order ?? defaultTableParams.sortOrder,
      sortField: sortByColumn.field ?? defaultTableParams.sortField,
    });
  };

  const toggleAllRows = () => {
    // all rows are expanded, so it collaps all rows
    if (expandedRowKeys.length === (data?.length ?? 0)) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys(data?.map((item) => item.id) ?? []);
    }
  };

  const onExpandRow = (expanded: boolean, record: SegmentHistory) => {
    const keys = [...expandedRowKeys];
    if (expanded) {
      keys.push(record.id);
    } else {
      const index = keys.indexOf(record.id);
      if (index !== -1) {
        keys.splice(index, 1);
      }
    }
    setExpandedRowKeys(keys);
  };

  return (
    <>
      <Table
        bordered
        scroll={{ x: 'max-content' }}
        // 90 is the value of src/business/App.scss - $header-height
        sticky={{ offsetHeader: mobile ? 0 : 90 }}
        size="small"
        dataSource={data}
        columns={columns}
        expandable={{
          expandedRowKeys: expandedRowKeys,
          onExpand: onExpandRow,
          expandedRowRender: (record: any) => (
            <Table
              dataSource={record.history}
              columns={expandedColumns}
              pagination={false}
            />
          ),
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <DownOutlined onClick={(event) => onExpand(record, event)} />
            ) : (
              <RightOutlined onClick={(event) => onExpand(record, event)} />
            ),
          columnTitle: (
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
              }}
              onClick={toggleAllRows}
            >
              {expandedRowKeys.length === data?.length ? (
                <DownOutlined
                  title={t(
                    'qualityControl.segmentHistory.expandedColumns.collapseAll',
                  )}
                />
              ) : (
                <RightOutlined
                  title={t(
                    'qualityControl.segmentHistory.expandedColumns.expandAll',
                  )}
                />
              )}
            </div>
          ),
        }}
        pagination={{
          current: tableParams.current,
          pageSize: tableParams.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_OPTIONS,
        }}
        onChange={handleChange}
        loading={loading}
        rowSelection={rowSelection}
      />
      <SegmentDetailsDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        afterOpenChange={handleAfterOpenChange}
        segmentId={selectedSegment?.id ?? null}
        segments={quarantineSegments}
        setSelectedSegment={setSelectedSegment}
        refetchData={refetch}
        mobile={mobile}
        isUserManager={isDataManager || isConstructionSiteManager}
      />
      <SegmentMobileDrawer
        open={drawerMobileOpen}
        onClose={handleCloseMobileDrawer}
        segmentId={selectedSegment?.id ?? null}
        segments={data}
        openDetailsDrawer={handleOpenDrawer}
      />
      {updateSegmentModalOpen ? (
        <UpdateSegmentModal
          isModalVisible={updateSegmentModalOpen}
          closeModal={handleCloseUpdateSegmentModal}
          segment={selectedSegment}
          refetchData={refetch}
          openAddManuelScanModal={handleOpenAddManualScanModal}
        />
      ) : null}
      {addManualScanModalOpen ? (
        <AddManualScanModal
          isModalVisible={addManualScanModalOpen}
          closeModal={handleCloseAddManualScanModal}
          segment={selectedSegment}
          refetchData={refetch}
        />
      ) : null}
      <BulkStatusModal
        open={bulkStatusModalOpen}
        onClose={onCloseBulkStatusModal ?? (() => {})}
        total={total}
        onSuccess={refetch}
        onStartPartialSelection={handleStartPartialSelection}
      />
      {selectionModeActive && !confirmModalOpen ? (
        <BulkStatusSelectionBar
          onCancel={resetPartialSelection}
          onValidate={() => setConfirmModalOpen(true)}
        />
      ) : null}
      {pendingBulkValues ? (
        <BulkStatusConfirmModal
          open={confirmModalOpen}
          onCancel={() => setConfirmModalOpen(false)}
          onSuccess={handlePartialUpdateSuccess}
          newStatus={pendingBulkValues.newStatus}
          scanSiteId={pendingBulkValues.scanSiteId}
          total={total}
          includedSegmentIds={Array.from(includedSegmentIds)}
        />
      ) : null}
    </>
  );
};
